import {
  Phase,
  Minimum,
  Price,
  DisabledReasonType,
  ValidationResult
} from 'modules/Cube/domain/cube.domain.types'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useNavigate } from 'react-router-dom'
import deepmerge from 'deepmerge'

export type PhaseHookInterface = {
  showProductSearch: boolean
  setShowAddProductSearch: (show: boolean) => void
  features: {
    addDiscount: {
      handler: () => void
      disabled: boolean
      reason: string
    }
    addMinimum: {
      handler: () => void
      disabled: boolean
      reason: string
    }
    addProduct: {
      handler: () => void
      disabled: boolean
      reason: string
    }
    addCredit: {
      handler: () => void
      disabled: boolean
      reason: string
      // Only shown when the embedded credit-grants feature is enabled.
      visible: boolean
    }
  }
  expanded: boolean
  updateExpanded: (newState: boolean) => void
  validationErrors: ValidationResult[]
  phaseDates: {
    start?: Date | undefined
    end?: Date | undefined
  }
  refs: {
    durationInput: React.RefObject<HTMLDivElement>
  }
  isHighlighted: boolean
  onClick: () => void
}

type UsePhase = (props: {
  phaseId: Phase['id']
  expanded: boolean
  focusDurationOnLoad?: boolean
  onExpand: (expanded: boolean) => void
}) => PhaseHookInterface

export const getPriceGroups =
  (activeVersionMinimums: Minimum[]) =>
  (
    activeVersionPrices: Price[]
  ): {
    minimum?: Minimum
    prices: Price[]
  }[] =>
    Object.values(
      activeVersionPrices.reduce((acc, price) => {
        if (!('usageMetricId' in price.structure)) {
          return deepmerge(acc, {
            'no-minimum': {
              prices: [price]
            }
          })
        }

        const matchingMinimum = activeVersionMinimums.find(
          minimum =>
            minimum.scope.target === 'allUsage' ||
            minimum.scope.priceIds.includes(price.id)
        )

        if (!matchingMinimum) {
          return deepmerge(acc, {
            'no-minimum': {
              prices: [price]
            }
          })
        }

        return deepmerge(acc, {
          [matchingMinimum.id]: {
            minimum: matchingMinimum,
            prices: [price]
          }
        })
      }, {})
    )

/**
 * Shared by every map below, so hovering any disabled control says why rather
 * than leaving the editor's banner to carry the explanation alone.
 */
const SCHEDULE_BEING_AMENDED_REASON =
  'This schedule cannot be edited while it is being amended by a quote'

const ADD_DISCOUNT_DISABLED_REASONS: Partial<
  Record<DisabledReasonType, string>
> = {
  [DisabledReasonType.GlobalDiscountExists]:
    'Cannot add another discount when a global discount already exists',
  [DisabledReasonType.NoValidDiscounts]:
    'No more discounts can be added to this phase',
  [DisabledReasonType.PhaseStartTransition]:
    'Change the start and/or end date of this phase to be able to add discounts',
  [DisabledReasonType.PhaseEndTransition]:
    'Change the start and/or end date of this phase to be able to add discounts',
  [DisabledReasonType.CompletedSchedule]:
    'Cannot add a discount to a completed schedule',
  [DisabledReasonType.CompletedPhase]:
    'Cannot add a discount to a completed phase',
  [DisabledReasonType.BeforeAmendmentEffectiveFrom]:
    "Cannot add a discount to a phase before the amendment's effective date",
  [DisabledReasonType.ScheduleLockedByAmendment]: SCHEDULE_BEING_AMENDED_REASON
}

const ADD_PRODUCT_DISABLED_REASONS: Partial<
  Record<DisabledReasonType, string>
> = {
  [DisabledReasonType.CompletedPhase]:
    'Cannot add a product to a completed phase',
  [DisabledReasonType.CompletedSchedule]:
    'Cannot add a product to a completed schedule',
  [DisabledReasonType.BeforeAmendmentEffectiveFrom]:
    "Cannot add a product to a phase before the amendment's effective date",
  [DisabledReasonType.ScheduleLockedByAmendment]: SCHEDULE_BEING_AMENDED_REASON
}

const ADD_MINIMUM_DISABLED_REASONS: Partial<
  Record<DisabledReasonType, string>
> = {
  [DisabledReasonType.MaxMinimums]:
    'Cannot add another minimum when a minimum already exists for this phase',
  [DisabledReasonType.NoValidPrices]:
    'Add a usage based price in order to add a minimum',
  [DisabledReasonType.CompletedSchedule]:
    'Cannot add a minimum to a completed schedule',
  [DisabledReasonType.CompletedPhase]:
    'Cannot add a minimum to a completed phase',
  [DisabledReasonType.BeforeAmendmentEffectiveFrom]:
    "Cannot add a minimum to a phase before the amendment's effective date",
  [DisabledReasonType.ScheduleLockedByAmendment]: SCHEDULE_BEING_AMENDED_REASON
}

const ADD_CREDIT_DISABLED_REASONS: Partial<Record<DisabledReasonType, string>> =
  {
    // A grant scopes to the phase's prices, so it needs a product first, and v1
    // enforces a single grant per phase on the FE (backend check to follow).
    [DisabledReasonType.PhaseHasNoPrices]:
      'Add a product in order to add credits',
    [DisabledReasonType.MaxCreditGrants]: 'Only one credit grant per phase',
    [DisabledReasonType.ScheduleLockedByAmendment]:
      SCHEDULE_BEING_AMENDED_REASON,
    [DisabledReasonType.CompletedPhase]:
      'Cannot add credits to a completed phase',
    [DisabledReasonType.CompletedSchedule]:
      'Cannot add credits to a completed schedule',
    [DisabledReasonType.BeforeAmendmentEffectiveFrom]:
      "Cannot add credits to a phase before the amendment's effective date",
    [DisabledReasonType.PendingApproval]:
      'Cannot add credits while approval is pending',
    [DisabledReasonType.CantChangeWhenPreparingSignatureRequest]:
      'Cannot add credits while preparing a signature request',
    [DisabledReasonType.CantChangeWhenAccepted]:
      'Cannot add credits to an accepted quote',
    [DisabledReasonType.CantChangeWhenExecuted]:
      'Cannot add credits to an executed quote',
    [DisabledReasonType.CantChangeWhenArchived]:
      'Cannot add credits to an archived quote'
  }

export const getAddCreditDisabledReason = (
  reasonType?: DisabledReasonType
): string => {
  if (!reasonType) {
    return ''
  }

  return ADD_CREDIT_DISABLED_REASONS[reasonType] ?? ''
}

export const usePhase: UsePhase = props => {
  const [showAddProductSearch, setShowAddProductSearch] = useState(false)
  const cubeContext = useCubeContext()
  const navigate = useNavigate()

  const durationInputRef = useRef<HTMLInputElement>(null)

  const resolvedPhase = useMemo(() => {
    return cubeContext.queries.resolvedPhases[props.phaseId]
  }, [props.phaseId, cubeContext.queries.resolvedPhases])

  const addProductDisabled = useMemo(() => {
    return !cubeContext.queries.availableFeatures.phases[resolvedPhase.id].phase
      .product.add.available.enabled
  }, [cubeContext, resolvedPhase.id])

  const showProductSearch = useMemo(() => {
    return !addProductDisabled && showAddProductSearch
  }, [addProductDisabled, showAddProductSearch])

  const availableFeaturesForVersion = useMemo(() => {
    return cubeContext.queries.availableFeatures.phases[resolvedPhase.id]
  }, [cubeContext.queries.availableFeatures, resolvedPhase.id])

  const features = useMemo(() => {
    return {
      addProduct: {
        handler: () => {
          setShowAddProductSearch(true)
        },
        disabled:
          !availableFeaturesForVersion.phase.product.add.available.enabled,
        reason:
          ADD_PRODUCT_DISABLED_REASONS[
            availableFeaturesForVersion.phase.product.add.reasons[0]?.reasonType
          ] ?? ''
      },
      addCredit: {
        handler: () => {
          navigate(`./credit-editor/${props.phaseId}`)
        },
        disabled:
          !availableFeaturesForVersion.phase.creditGrant.add.available.enabled,
        reason: getAddCreditDisabledReason(
          availableFeaturesForVersion.phase.creditGrant.add.reasons[0]
            ?.reasonType
        ),
        visible:
          availableFeaturesForVersion.phase.creditGrant.add.available.visible
      },
      addDiscount: {
        handler: () => {
          navigate(`./discount-editor/${props.phaseId}`)
        },
        disabled:
          !availableFeaturesForVersion.phase.discount.add.available.enabled,
        reason:
          ADD_DISCOUNT_DISABLED_REASONS[
            availableFeaturesForVersion.phase.discount.add.reasons[0]
              ?.reasonType
          ] ?? ''
      },
      addMinimum: {
        handler: () => {
          navigate(`./minimum-editor/${props.phaseId}`)
        },
        disabled:
          !availableFeaturesForVersion.phase.minimum.add.available.enabled,
        reason:
          ADD_MINIMUM_DISABLED_REASONS[
            availableFeaturesForVersion.phase.minimum.add.reasons[0]?.reasonType
          ] ?? ''
      }
    }
  }, [navigate, props.phaseId, availableFeaturesForVersion])

  useLayoutEffect(() => {
    if (props.focusDurationOnLoad) {
      durationInputRef.current?.focus()
    }
  }, [props.focusDurationOnLoad])

  const validationErrors = useMemo(() => {
    return [
      ...(cubeContext.queries.validation.activeValidationResults?.common
        .phases?.[props.phaseId]?.prices ?? [])
    ]
  }, [cubeContext.queries.validation.activeValidationResults, props.phaseId])

  const updateExpanded = useCallback(
    (newState: boolean) => {
      if (!newState && validationErrors.length > 0) {
        return
      }
      props.onExpand(newState)
    },
    [props.onExpand, validationErrors]
  )

  useEffect(() => {
    if (validationErrors.length > 0 && !props.expanded) {
      updateExpanded(true)
    }
  }, [validationErrors, props.expanded, updateExpanded])

  return {
    showProductSearch,
    setShowAddProductSearch,
    validationErrors,
    expanded: props.expanded,
    updateExpanded,
    features,
    phaseDates: resolvedPhase.dates.absolute,
    refs: {
      durationInput: durationInputRef
    },
    isHighlighted:
      cubeContext.queries.rawData.editor.highlightedEntityIds.includes(
        props.phaseId
      ),
    onClick: () => {
      cubeContext.mutators.updateEditor({
        highlightedEntityIds:
          cubeContext.queries.rawData.editor.highlightedEntityIds.filter(
            phaseId => phaseId !== props.phaseId
          )
      })
    }
  }
}
